import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, X, Check, User, MapPin, Clock, ArrowLeft, Inbox } from "lucide-react";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReceivedInterestsPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [processing, setProcessing] = useState(null);
  const token = localStorage.getItem("liztv_token");
  const storedUser = localStorage.getItem("liztv_user");
  const userId = storedUser ? JSON.parse(storedUser)?.id : null;

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/messages/inbox`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleAccept = async (msgId) => {
    setProcessing(msgId);
    try {
      const res = await fetch(`${API_URL}/api/messages/${msgId}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        navigate(`/chat/${data.chat_room_id}`);
      } else {
        const err = await res.json();
        alert(err.detail || "수락에 실패했습니다.");
      }
    } catch {
      alert("오류가 발생했습니다.");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (msgId) => {
    if (!window.confirm("정말 거절하시겠습니까?")) return;
    setProcessing(msgId);
    try {
      const res = await fetch(`${API_URL}/api/messages/${msgId}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, status: "rejected" } : m)));
        setSelectedMsg(null);
        alert("정중히 거절되었습니다.");
      }
    } catch {
      alert("오류가 발생했습니다.");
    } finally {
      setProcessing(null);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`;
    return `${Math.floor(diff / 86400000)}일 전`;
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-stone-500 mb-4">로그인이 필요합니다.</p>
          <Button onClick={() => navigate("/")}>홈으로</Button>
        </div>
      </div>
    );
  }

  const pendingMessages = messages.filter((m) => !m.status || m.status === "pending");
  const handledMessages = messages.filter((m) => m.status === "accepted" || m.status === "rejected");

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-4xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-stone-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-800" data-testid="received-interests-title">받은 관심</h1>
            <p className="text-sm text-stone-500">회원님에게 관심을 보낸 분들이에요</p>
          </div>
          <span className="ml-auto px-3 py-1 bg-burgundy-100 text-burgundy-600 rounded-full text-sm font-medium" data-testid="pending-count">
            {pendingMessages.length}건 대기 중
          </span>
        </div>

        {loading ? (
          <div className="text-center py-20 text-stone-400">불러오는 중...</div>
        ) : pendingMessages.length === 0 && handledMessages.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-stone-100">
            <Inbox className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500 text-lg font-medium">아직 받은 관심이 없습니다</p>
            <p className="text-stone-400 text-sm mt-1">다른 회원들이 관심을 보내면 여기에 표시됩니다</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Pending messages */}
            {pendingMessages.map((msg) => (
              <motion.div
                key={msg.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border border-stone-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedMsg(msg)}
                data-testid={`interest-card-${msg.id}`}
              >
                <div className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-burgundy-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-burgundy-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-stone-800">{msg.sender_name}</span>
                      {!msg.is_read && (
                        <span className="w-2 h-2 bg-red-500 rounded-full" />
                      )}
                    </div>
                    <p className="text-sm text-stone-500 truncate">{msg.content}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-stone-400">{formatTime(msg.created_at)}</p>
                    <div className="flex gap-1.5 mt-2">
                      <Button
                        size="sm"
                        className="bg-burgundy-500 hover:bg-burgundy-600 text-white h-8 px-3 text-xs"
                        onClick={(e) => { e.stopPropagation(); handleAccept(msg.id); }}
                        disabled={processing === msg.id}
                        data-testid={`accept-btn-${msg.id}`}
                      >
                        <Check className="w-3 h-3 mr-1" />수락
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-3 text-xs border-stone-200"
                        onClick={(e) => { e.stopPropagation(); handleReject(msg.id); }}
                        disabled={processing === msg.id}
                        data-testid={`reject-btn-${msg.id}`}
                      >
                        <X className="w-3 h-3 mr-1" />거절
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Handled messages */}
            {handledMessages.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <p className="text-sm font-medium text-stone-400">처리 완료</p>
                </div>
                {handledMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className="bg-stone-50 rounded-xl border border-stone-100 p-4 flex items-center gap-4 opacity-60"
                  >
                    <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-stone-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-stone-600">{msg.sender_name}</span>
                      <p className="text-sm text-stone-400 truncate">{msg.content}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${msg.status === "accepted" ? "bg-green-100 text-green-600" : "bg-stone-200 text-stone-500"}`}>
                      {msg.status === "accepted" ? "수락됨" : "거절됨"}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Profile Preview Modal */}
      <AnimatePresence>
        {selectedMsg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedMsg(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="interest-profile-modal"
            >
              <div className="bg-gradient-to-r from-burgundy-500 to-burgundy-600 p-6 text-center">
                <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <User className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-white text-lg font-bold">{selectedMsg.sender_name}</h3>
              </div>

              <div className="p-6">
                <div className="bg-stone-50 rounded-xl p-4 mb-5">
                  <p className="text-xs text-stone-400 mb-1 flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" />받은 메시지
                  </p>
                  <p className="text-stone-700">{selectedMsg.content}</p>
                  <p className="text-xs text-stone-400 mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />{formatTime(selectedMsg.created_at)}
                  </p>
                </div>

                {(!selectedMsg.status || selectedMsg.status === "pending") && (
                  <div className="flex gap-3">
                    <Button
                      className="flex-1 bg-burgundy-500 hover:bg-burgundy-600 text-white"
                      onClick={() => handleAccept(selectedMsg.id)}
                      disabled={processing === selectedMsg.id}
                      data-testid="modal-accept-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />대화 수락하기
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-stone-200"
                      onClick={() => handleReject(selectedMsg.id)}
                      disabled={processing === selectedMsg.id}
                      data-testid="modal-reject-btn"
                    >
                      거절하기
                    </Button>
                  </div>
                )}

                {selectedMsg.status === "accepted" && (
                  <Button className="w-full bg-green-500 hover:bg-green-600 text-white" disabled>
                    <Check className="w-4 h-4 mr-2" />수락 완료
                  </Button>
                )}
                {selectedMsg.status === "rejected" && (
                  <Button className="w-full" variant="outline" disabled>
                    거절됨
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
